import { useEffect } from 'react'
import { useRouter } from 'next/router'

import { isAuthenticated } from '~/auth/slices/auth.slice'
import useCurrentUser from '~/hooks/useCurrentUser'
import { useAppSelector } from '~/redux/hooks'
import ROUTES from '~/shared/constants/routes.const'
import { AppLanguage } from '~/types/locale'

// A signed-in user for whom a subscription cannot apply — a country without prices, the Ukrainian
// locale, or a role that never pays (`isSubscriptionApplicable` comes back false from /users/me for all
// of them) — has nothing to buy on a price step, so they go to the dashboard instead. Control's intro
// already keeps such users out of the paid funnel while they are logged out; this covers the moment
// after they sign in (SSO returns them to the funnel) on both arms.
export default function useRedirectIfSubscriptionNotApplicable(enabled = true): { isBlocking: boolean } {
  const router = useRouter()
  const authenticated = useAppSelector(isAuthenticated)
  const user = useCurrentUser()
  const isUkrainianLocale = router.locale === AppLanguage.UA
  const notApplicable = user != null && user.isSubscriptionApplicable === false
  const shouldRedirect = enabled && authenticated && (isUkrainianLocale || notApplicable)
  // /users/me is still in flight right after login; hold the price content back until it has answered
  // so it never renders for someone about to be sent away.
  const isResolving = enabled && authenticated && user == null && !isUkrainianLocale

  useEffect(() => {
    if (shouldRedirect) {
      router.replace(ROUTES.DASHBOARD)
    }
  }, [shouldRedirect, router])

  return { isBlocking: shouldRedirect || isResolving }
}
